import React, { useState, useCallback } from "react";
import { 
  SuccessMessage,
  FailMessage,
  Loading,
  FormatValueToDecimal,
  CompanySelect,
  FormatDateToMDY,
  DateSelect,
  FormatDateToWord,
  ChartOfAccountsMultipleSelect
} from "../../common/APICommon";
import { apiUrl } from "../../utils/Configuration";
import axios from 'axios';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { PDFDocument } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import NoAccess from "../NoAccess";
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';

export default function GeneralLedgerReport() {
//#region Others
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failMessage, setFailMessage] = useState("");
//#endregion






//#region Variables 
  const [company, setCompany] = useState(null);
  const today = new Date().toISOString().split("T")[0];
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  
  const [glAccounts, setGLAccounts] = useState([]);
  const [showGLAccountSelectForm, setShowGLAccountSelectForm] = useState(false);
  const openGLAccountSelectForm = () => {
    setShowGLAccountSelectForm(true);
  }
  const closeGLAccountSelectForm = () => {
    setShowGLAccountSelectForm(false);
  }
//#endregion






//#region Generate
  const fetchReportData = useCallback(async (_data) => {
    const body = {
      glAccountIds: glAccounts?.map((acc) => {
        return acc.id;
      }) ?? [],
      dateFrom: dateFrom,
      dateTo: dateTo,
    }

    try {
      // const response = await axios.post(`${apiUrl}/api/Report/report-data/statement-of-account`, body, {
      const response = await axios.post(`${apiUrl}/api/Report/report-data/general-ledger-report`, body, {
        headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return null
    }
  }, [glAccounts, dateFrom, dateTo]);

  const generateHandleClick = async () => {
    if (!dateFrom || !dateTo || !company) {
        setFailMessage("Please fill up required fields");
        return;
    }

    setLoading(true);
    const _data = await fetchReportData();

    if (!_data || _data?.length <= 0) {
        setFailMessage("No record found");
        setLoading(false);
        return;
    }

    try {
        const pdfUrl = `${window.location.origin}/docs/GL_ACCOUNT_REPORT.pdf`;
        const templateBytes = await fetch(pdfUrl).then((r) => r.arrayBuffer());
        const templateDoc = await PDFDocument.load(templateBytes);
        
        const response = await fetch(pdfUrl);
        const pdfBytes = await response.arrayBuffer();
        const pdfDoc = await PDFDocument.load(pdfBytes);

        pdfDoc.registerFontkit(fontkit);
        const fontResponse = await fetch("/calibri.ttf");
        const fontBytes = await fontResponse.arrayBuffer();
        const calibriFont = await pdfDoc.embedFont(fontBytes);

        const boldResponse = await fetch("/calibrib.ttf");
        const boldBytes = await boldResponse.arrayBuffer();
        const calibriBold = await pdfDoc.embedFont(boldBytes);

        const wrapText = (text, maxWidth, font, fontSize) => {
          if (!text) return [""];
          
          const words = text.split(" ");
          const lines = [];
          let currentLine = "";

          for (const word of words) {
            const testLine = currentLine ? `${currentLine} ${word}` : word;

            if (font.widthOfTextAtSize(testLine, fontSize) <= maxWidth) {
              currentLine = testLine;
            } else {
              // If the word itself exceeds maxWidth, force character-by-character wrapping
              if (font.widthOfTextAtSize(word, fontSize) > maxWidth) {
                if (currentLine) lines.push(currentLine);
                currentLine = "";

                let chunk = "";
                for (const char of word) {
                  if (font.widthOfTextAtSize(chunk + char, fontSize) <= maxWidth) {
                    chunk += char;
                  } else {
                    lines.push(chunk);
                    chunk = char;
                  }
                }
                currentLine = chunk;
              } else {
                if (currentLine) lines.push(currentLine);
                currentLine = word;
              }
            }
          }

          if (currentLine) lines.push(currentLine);
          return lines;
        };

        const drawHeader = (targetPage) => {
          targetPage.drawText(company.name?.toString() ?? "", { x: 35, y: 571, size: 16, font: calibriBold });
          targetPage.drawText(company.address?.toString() ?? "", { x: 35, y: 561, size: 8, font: calibriFont });
          targetPage.drawText(company.tin?.toString() ?? "", { x: 35, y: 551, size: 8, font: calibriFont });
          
          targetPage.drawText(`${FormatDateToWord(dateFrom)} to ${FormatDateToWord(dateTo)}`, { x: 85, y: 526, size: 8, font: calibriBold });
        };

        let currentPage = pdfDoc.getPages()[0];
        drawHeader(currentPage);

        let yPosition = 456;
        const bottomThreshold = 80;
        const lineHeight = 12;

        const colWidthDocNo = 40;
        const colWidthAccountName = 80;
        const colWidthRefNo = 80;
        const colWidthCompany = 90;
        const colWidthRemarks = 90;

        const addNewPage = async () => {
          const [templatePage] = await pdfDoc.copyPages(templateDoc, [0]);
          currentPage = pdfDoc.addPage(templatePage);
          drawHeader(currentPage);
          yPosition = 456;
        };

        let totalDebit = 0;
        let totalCredit = 0;
        let totalAmount = 0;

        for (const record of _data) {
          if (yPosition < bottomThreshold) {
            await addNewPage();
          }

          let trn = "REC"

          if(record.vendorId){
            trn = "PAY"
          }
          
          currentPage.drawText(trn, { x: 40, y: yPosition, size: 8, font: calibriFont });

          const docNoLines = wrapText(record.referenceNumber ?? "", colWidthDocNo, calibriFont, 8);
          const accountNameLines = wrapText(record.glAccountName ?? "", colWidthAccountName, calibriFont, 8);
          const refNoLines = wrapText(record.referenceNumber ?? "", colWidthRefNo, calibriFont, 8);
          const companyLines = wrapText(record.company?.companyName1 ?? "", colWidthCompany, calibriFont, 8);
          const remarksLines = wrapText(record.description ?? "", colWidthRemarks, calibriFont, 8);

          const maxLines = Math.max(docNoLines.length, accountNameLines.length, refNoLines.length, companyLines.length, remarksLines.length, 1);
          const rowHeight = maxLines * lineHeight;

          // --- PAGE BREAK CHECK FOR ITEM ROW ---
          if (yPosition - rowHeight < bottomThreshold) {
            await addNewPage();
          }

          // Draw wrapped description
          for (let i = 0; i < docNoLines.length; i++) {
            currentPage.drawText(docNoLines[i], {
              x: 65,
              y: yPosition - i * lineHeight,
              size: 8,
              font: calibriFont,
            });
          }

          currentPage.drawText(`${record.glAccountNumber ?? ""}`, { x: 110, y: yPosition, size: 8, font: calibriFont });

          // Draw wrapped description
          for (let i = 0; i < accountNameLines.length; i++) {
            currentPage.drawText(accountNameLines[i], {
              x: 153,
              y: yPosition - i * lineHeight,
              size: 8,
              font: calibriFont,
            });
          }

          // Draw wrapped description
          for (let i = 0; i < refNoLines.length; i++) {
            currentPage.drawText(refNoLines[i], {
              x: 265,
              y: yPosition - i * lineHeight,
              size: 8,
              font: calibriFont,
            });
          }

          currentPage.drawText(`${FormatDateToMDY(record.transactionDate ?? "")}`, { x: 340, y: yPosition, size: 8, font: calibriFont });

          const date = record.transactionDate
            ? new Date(record.transactionDate)
            : null;

          const month = date ? date.getMonth() + 1 : "";
          const year = date ? date.getFullYear() : "";

          currentPage.drawText(`${month}`, { x: 390, y: yPosition, size: 8, font: calibriFont });
          currentPage.drawText(`${year}`, { x: 420, y: yPosition, size: 8, font: calibriFont });

          var debit = record.debitCredit === "Debit" ? record.amount : 0;
          var credit = record.debitCredit === "Credit" ? record.amount : 0;

          totalDebit += debit;
          totalCredit += credit;
          totalAmount += record.amount;

          currentPage.drawText(`${FormatValueToDecimal(debit)}`, { x: 453, y: yPosition, size: 8, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(credit)}`, { x: 502, y: yPosition, size: 8, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(record.amount)}`, { x: 550, y: yPosition, size: 8, font: calibriFont });

          
          // Draw wrapped description
          for (let i = 0; i < companyLines.length; i++) {
            currentPage.drawText(companyLines[i], {
              x: 605,
              y: yPosition - i * lineHeight,
              size: 8,
              font: calibriFont,
            });
          }

          
          // Draw wrapped description
          for (let i = 0; i < remarksLines.length; i++) {
            currentPage.drawText(remarksLines[i], {
              x: 690,
              y: yPosition - i * lineHeight,
              size: 8,
              font: calibriFont,
            });
          }
          
          yPosition -= rowHeight + 5;
        }

        if (yPosition < 100) {
          await addNewPage();
        }
  
        currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 757, y: yPosition }, thickness: 1 });
        yPosition -= 15;
        currentPage.drawText(`${FormatValueToDecimal(totalDebit)}`, { x: 448, y: yPosition, size: 8, font: calibriBold });
        currentPage.drawText(`${FormatValueToDecimal(totalCredit)}`, { x: 502, y: yPosition, size: 8, font: calibriBold });
        currentPage.drawText(`${FormatValueToDecimal(totalAmount)}`, { x: 550, y: yPosition, size: 8, font: calibriBold });

        const modifiedPdfBytes = await pdfDoc.save();
        const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `GL_ACCOUNT_REPORT.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    } catch (error) {
        console.error("Error:", error);
        setFailMessage("Error processing PDF");
    }
    setLoading(false);
  };

  const exportExcel = async () => {
    if (!dateFrom || !dateTo || !company) {
      setFailMessage("Please fill up required fields");
      return;
    }

    setLoading(true);

    try {
      const _data = await fetchReportData();

      if (!_data || _data?.length <= 0) {
        setFailMessage("No record found");
        setLoading(false);
        return;
      }

      // 1. Create Workbook and Worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("GL Activity Report");

      // 2. Add Company & Report Header Information
      worksheet.addRow([company.name?.toUpperCase() ?? ""]).font = {
        bold: true,
        size: 14,
      };
      worksheet.addRow([company.address ?? ""]);
      worksheet.addRow([company.tin ? `TIN: ${company.tin}` : ""]);
      worksheet.addRow([]); // Blank line

      worksheet.addRow(["GL Activity Report"]).font = { bold: true, size: 12 };
      worksheet.addRow([
        `Period Cover: ${FormatDateToWord(dateFrom)} to ${FormatDateToWord(dateTo)}`,
      ]);
      worksheet.addRow([`Date Printed: ${FormatDateToWord(new Date())}`]);
      worksheet.addRow([]); // Blank line

      // 3. Define Table Columns (Matching the Image)
      const headerRow = worksheet.addRow([
        "Trn.",
        "Doc. No.",
        "Account No",
        "New Account Name",
        "GL Dept.",
        "Ref. No.",
        "Date",
        "Month",
        "Year",
        "Debit",
        "Credit",
        "Amount",
        "Customer / Supplier / Employee",
        "Remarks",
      ]);

      // Style the Header Row
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "444444" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // 4. Populate Data Rows
      let totalDebit = 0;
      let totalCredit = 0;
      let totalAmount = 0;

      _data.forEach((record) => {
        const trn = record.vendorId ? "PAY" : "REC";
        const amountVal = parseFloat(record.amount || 0);
        const debitVal = record.debitCredit === "Debit" ? amountVal : 0;
        const creditVal = record.debitCredit === "Credit" ? amountVal : 0;

        const dateObj = record.transactionDate
          ? new Date(record.transactionDate)
          : null;
        const month = dateObj ? dateObj.getMonth() + 1 : "";
        const year = dateObj ? dateObj.getFullYear() : "";

        const row = worksheet.addRow([
          trn,
          record.referenceNumber ?? "",
          record.glAccountNumber ?? "",
          record.glAccountName ?? "",
          record.glDept ?? "",
          record.referenceNumber ?? "",
          FormatDateToMDY(record.transactionDate ?? ""),
          month,
          year,
          debitVal,
          creditVal,
          amountVal,
          record.company?.companyName1 ?? record.vendorName ?? record.customerName ?? "",
          record.description ?? "",
        ]);

        // Alignments
        row.getCell(1).alignment = { horizontal: "center" }; // Trn
        row.getCell(7).alignment = { horizontal: "center" }; // Date
        row.getCell(8).alignment = { horizontal: "center" }; // Month
        row.getCell(9).alignment = { horizontal: "center" }; // Year

        // Number formatting for Currency columns (Debit: 10, Credit: 11, Amount: 12)
        [10, 11, 12].forEach((colIdx) => {
          const cell = row.getCell(colIdx);
          cell.numFmt = "#,##0.00";
          cell.alignment = { horizontal: "right" };
        });

        // Accumulate Totals
        totalDebit += debitVal;
        totalCredit += creditVal;
        totalAmount += amountVal;
      });

      // 5. Add Grand Total / Summary Footer
      worksheet.addRow([]); // Blank spacer row

      const footerRow = worksheet.addRow([
        "TOTAL",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        totalDebit,
        totalCredit,
        totalAmount,
        "",
        "",
      ]);

      footerRow.font = { bold: true };

      // Format Totals and add accounting borders (single top, double bottom)
      [10, 11, 12].forEach((idx) => {
        const cell = footerRow.getCell(idx);
        cell.numFmt = "#,##0.00";
        cell.alignment = { horizontal: "right" };
        cell.border = {
          top: { style: "thin" },
          bottom: { style: "double" },
        };
      });

      // 6. Set Responsive Column Widths
      worksheet.columns = [
        { width: 8 },  // Trn.
        { width: 20 }, // Doc. No.
        { width: 14 }, // Account No
        { width: 28 }, // New Account Name
        { width: 12 }, // GL Dept.
        { width: 20 }, // Ref. No.
        { width: 14 }, // Date
        { width: 8 },  // Month
        { width: 8 },  // Year
        { width: 16 }, // Debit
        { width: 16 }, // Credit
        { width: 16 }, // Amount
        { width: 32 }, // Customer / Supplier / Employee
        { width: 20 }, // Remarks
      ];

      // 7. Generate and Trigger Download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `GL_Activity_Report_${new Date().getTime()}.xlsx`);
    } catch (error) {
      console.error("Excel Error:", error);
      setFailMessage("Error generating Excel file");
    }

    setLoading(false);
  };
//#endregion






//#region Access Template
  const accountData = JSON.parse(localStorage.getItem("accountData"));
  
  if (!accountData?.accessTemplate?.hasGeneralLedgerReport) {
    return <NoAccess />;
  }
//#endregion






//#region UI
  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <HistoryEduIcon fontSize="large" className="text-theme-color" />
              {`General Ledger Report`}
            </h2>
            <p className="text-gray-500 mt-1">Complete activity and balance of account.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700">
              Accounts
            </label>

            {/* Selected Accounts */}
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              {glAccounts?.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {glAccounts.map((account) => (
                    <div
                      key={account.id}
                      className="flex items-center justify-between gap-3 px-3 py-2 bg-white hover:bg-gray-50"
                    >
                      <span className="text-sm text-gray-800">
                        {account.accountName}
                      </span>

                      <button
                        type="button"
                        onClick={() =>
                          setGLAccounts((prev) =>
                            prev.filter((acc) => acc.id !== account.id)
                          )
                        }
                        className="text-red-500 hover:text-red-700 transition"
                        title="Remove account"
                      >
                        <RemoveCircleIcon />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-3 py-2 text-sm text-gray-400">
                  No accounts selected
                </div>
              )}

              {/* Select Accounts Button */}
              <div className="flex justify-end border-t border-gray-200 p-2 bg-gray-50">
                <button
                  type="button"
                  onClick={openGLAccountSelectForm}
                  className="flex items-center gap-1 bg-theme-color hover:bg-theme-color-h text-white text-sm px-3 py-2 rounded-lg transition"
                >
                  <ManageSearchIcon fontSize="small" />
                  Select Accounts
                </button>
              </div>
            </div>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Company <span className="text-red-500">*</span>
            </label>
            <CompanySelect data={company} setData={setCompany} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Date From <span className="text-red-500">*</span>
            </label>
            <DateSelect data={dateFrom} setData={setDateFrom} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Date To <span className="text-red-500">*</span>
            </label>
            <DateSelect data={dateTo} setData={setDateTo} isEditable={true}/>
          </div>
        </div>

        <div className="mt-5 p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
          <h2 className="text-lg font-bold text-gray-800 mb-6 px-2">Generate General Ledger Report</h2>

          {/* Header Row - Hidden on Mobile */}
          <div className="hidden md:grid grid-cols-12 gap-4 px-4 mb-2 text-xs font-black uppercase tracking-widest text-gray-400">
            <div className="col-span-2">Report Category</div>
            <div className="col-span-5">PDF Reports</div>
            <div className="col-span-5 border-l border-gray-100">Excel Reports</div>
          </div>

          <div className="space-y-3">
            {[
              { 
                label: "Summary", 
                pdf_summarized: generateHandleClick, 
                excel_summarized: exportExcel, // Replace with your Excel function
              },
            ].map((group, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-12 items-center gap-4 p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors">
                
                {/* Label */}
                <div className="col-span-2">
                  <span className="text-sm font-bold text-gray-700">{group.label}</span>
                </div>

                {/* PDF Column */}
                <div className="col-span-5 flex flex-wrap gap-2">
                  {group.pdf_detailed &&
                    <button 
                      onClick={group.pdf_detailed} 
                      className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-100 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-red-100 transition-all active:scale-95"
                      title="Download PDF Detailed"
                    >
                      <RestartAltIcon sx={{ fontSize: 16 }} /> Detailed
                    </button>
                  }

                  {group.pdf_summarized &&
                    <button 
                      onClick={group.pdf_summarized} 
                      className="flex items-center gap-2 bg-white text-gray-700 border border-gray-300 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-gray-50 transition-all active:scale-95"
                    >
                      Summarized
                    </button>
                  }

                  {group.pdf_extra && (
                    <button 
                      onClick={group.pdf_extra} 
                      className="flex items-center gap-2 bg-blue-50 text-blue-700 border border-blue-100 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-blue-100 transition-all active:scale-95"
                    >
                      Summarized w/ %
                    </button>
                  )}
                </div>

                {/* Excel Column */}
                <div className="col-span-5 flex flex-wrap gap-2 border-t md:border-t-0 md:border-l border-gray-100 pt-3 md:pt-0">
                  {group.excel_detailed &&
                    <button 
                      onClick={group.excel_detailed} 
                      className="flex items-center gap-2 bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-green-700 shadow-sm transition-all active:scale-95"
                    >
                      Excel Detailed
                    </button>
                  }
                  
                  {group.excel_summarized &&
                    <button 
                      onClick={group.excel_summarized} 
                      className="flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-green-100 transition-all active:scale-95"
                    >
                      Excel Summ.
                    </button>
                  }
                  
                  
                  { group.excel_extra &&
                    <button 
                      onClick={group.excel_extra} 
                      className="flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-green-100 transition-all active:scale-95"
                    >
                      Excel Summ. w/ %
                    </button>
                  }
                </div>

              </div>
            ))}
          </div>
        </div>

        <button onClick={generateHandleClick} className="hidden mt-10 flex items-center gap-2 bg-theme-color text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-theme-color-h transition shadow-md">
          <RestartAltIcon fontSize="small" /> Generate
        </button>
      </div>
      
      <ChartOfAccountsMultipleSelect 
        visible={showGLAccountSelectForm} 
        onClose={closeGLAccountSelectForm}
        modalName={"Select Account"}
        data={glAccounts} setData={setGLAccounts}
      />

      <div className="fixed top-5 right-5 z-50 flex flex-col space-y-2">
        <SuccessMessage message={successMessage} onClose={() => setSuccessMessage("")} />
        <FailMessage message={failMessage} onClose={() => setFailMessage("")} />
      </div>

      { loading && <Loading />}
    </>
  );
//#endregion
}